import i18n, { LOCALIZED_PACK_UNITS } from './i18n';
import { getNumberFormatter } from './number-format';
import { gql } from '@apollo/client';
import { OrderFeeDisplayType, OrderItemPriceFieldsFragment } from '@/types';
import { PackUnit } from '@/enums';
import { PromotedVariantPackInfo } from './volume-promotions-types';
import { toFixedDecimalPercentage } from './percentage';

export const DEFAULT_CURRENCY_CODE = 'USD';
export const DEFAULT_CURRENCY_SYMBOL = '$';
export const ONE_CENT = 10000;

export const ORDER_FEES_FIELDS_FRAGMENT = gql`
  fragment OrderFeesFields on order_fees {
    amount_at_invoice_micros
    amount_at_order_micros
    display_type
    name
    uuid
  }
`;

export const ORDER_FEES_DETAILS_FRAGMENT = gql`
  fragment OrderFeesDetailsFields on GetOrderFeesResponse {
    amount_at_invoice_micros
    amount_at_order_micros
    display_type
    name
  }
`;

export const ORDER_ITEM_PRICE_FIELDS = gql`
  fragment PricePackFields on packs {
    unit_count
    uuid
  }

  fragment OrderItemPricesFields on order_item_prices {
    currency_code
    pack_quantity_at_invoice
    pack_quantity_at_order
    tax_at_order_micros
    unit_discount_price_at_order_micros
    unit_price_at_invoice_micros
    unit_price_at_order_micros
  }

  fragment OrderItemPriceFields on orders_items {
    order_item_prices {
      ...OrderItemPricesFields
    }
    pack {
      ...PricePackFields
    }
    pricing_pack {
      ...PricePackFields
    }
  }
`;

export const ORDER_DISPLAY_ITEM_PRICES_FRAGMENT = gql`
  fragment OrderDisplayItemPricesFields on orders {
    chat {
      display_item_prices
      uuid
    }
    supplier {
      supplier_metadata {
        display_item_prices
        uuid
      }
      uuid
    }
    uuid
  }
`;

// Type can be used for items, restaurant items, and order items
interface ItemSubtitleParamsType {
  currencyCode?: string | null;
  displayString: string;
  unitCount: number;
  unitPriceInMicros?: number | null;
}

interface PriceFieldsType {
  currencyCode?: string | null;
  quantity: number;
  unitCount: number;
  unitPriceInMicros?: number | null;
}

export const CENTS_CONVERSION = 10000;
export const MICRO_CONVERSION = 1000000;

export const CURRENCY_CODE_MAPPING: { [key: string]: string } = {
  AU: 'AUD',
  CA: 'CAD',
  MX: 'MXN',
  US: 'USD',
};

// Add currency symbol overrides for specific currency codes
export const CURRENCY_SYMBOL_OVERRIDES: Record<string, string> = {
  BMD: '$',
};

export const getCountryCurrencyCode = (countryCode: string) =>
  CURRENCY_CODE_MAPPING[countryCode] || DEFAULT_CURRENCY_CODE;

/**
 * Formats a currency amount from a micros value.
 *
 * Converts an amount in micros to a formatted currency string.
 *
 * @param {number} amountInMicros - Amount in micros
 * @param currencyCode?: string | null - ISO currency code (e.g., "USD", "EUR") or null/undefined
 * @param options?: Intl.NumberFormatOptions - Options for the Intl.NumberFormat constructor
 *
 * @returns {string} Formatted currency string (e.g., "$15.50")
 *
 * @example
 * formatCurrencyFromMicros(15500000, "USD") // Returns "$15.50"
 * formatCurrencyFromMicros(100000000, null) // Returns "$100.00" (defaults to USD)
 */
export const formatCurrency = (
  priceInMicros: number,
  currencyCode?: string | null,
  options?: Intl.NumberFormatOptions,
) => {
  const convertedPrice = priceInMicros / MICRO_CONVERSION;
  const code = currencyCode?.toUpperCase() || DEFAULT_CURRENCY_CODE;
  const formatter = getNumberFormatter(i18n.locale, {
    currency: code,
    style: 'currency',
    ...options,
  });

  const overrideSymbol = CURRENCY_SYMBOL_OVERRIDES[code];
  if (!overrideSymbol) {
    // If there's no override symbol, just return the formatted price
    return formatter.format(convertedPrice);
  }

  const parts = formatter.formatToParts(convertedPrice);
  return parts.reduce((result, part, index) => {
    // Skip literal spaces that are adjacent to currency symbols
    if (part.type === 'literal' && part.value.trim() === '') {
      const prevIsCurrency = index > 0 && parts[index - 1].type === 'currency';
      const nextIsCurrency =
        index < parts.length - 1 && parts[index + 1].type === 'currency';
      if (prevIsCurrency || nextIsCurrency) {
        return result;
      }
    }

    // Replace currency symbol with override
    return result + (part.type === 'currency' ? overrideSymbol : part.value);
  }, '');
};

export const formatPercent = (value: number) => {
  const formatter = getNumberFormatter(i18n.locale, {
    maximumFractionDigits: 2,
    style: 'percent',
  });
  return formatter.format(value);
};

export const formatUnitCount = (value: number) => {
  const formatter = getNumberFormatter(i18n.locale, {
    maximumFractionDigits: 2,
    style: 'decimal',
  });
  return formatter.format(value);
};

interface ItemPricesFields {
  currencyCode: string | null;
  quantity: number | null;
  unitPriceInMicros: number | null;
}

export const getOrderItemPricesFieldsToUse = (
  orderItem: OrderItemPriceFieldsFragment,
): ItemPricesFields | null => {
  const orderItemPrices = orderItem.order_item_prices;
  if (!orderItemPrices) {
    return null;
  }
  const currencyCode = orderItemPrices.currency_code || null;

  const unitPriceInMicros =
    orderItemPrices.unit_price_at_invoice_micros ??
    orderItemPrices.unit_discount_price_at_order_micros ??
    orderItemPrices.unit_price_at_order_micros ??
    null;

  const quantity =
    orderItemPrices.pack_quantity_at_invoice ??
    orderItemPrices.pack_quantity_at_order ??
    null;

  return {
    currencyCode,
    quantity,
    unitPriceInMicros,
  };
};

export const getTotalLineItemPrice = ({
  unitPriceInMicros,
  quantity,
  unitCount,
}: PriceFieldsType) => {
  if (typeof unitPriceInMicros !== 'number') {
    return null;
  }

  return unitPriceInMicros * quantity * unitCount;
};

interface GetPromotionPriceParamsType {
  basePriceInMicros?: number | null;
  promotedVariantPackInfo?: PromotedVariantPackInfo;
  unitCount: number;
}

export const getPromotionPricePerUnitInMicros = ({
  basePriceInMicros,
  promotedVariantPackInfo,
  unitCount,
}: GetPromotionPriceParamsType) => {
  if (!promotedVariantPackInfo) {
    return null;
  }

  let promotionFullPrice: number | null = null;

  // if promo has fixed price, use that regardless of base price
  if (typeof promotedVariantPackInfo.numAffected === 'number') {
    // TODO: BOGO promos (return null for now so we don't show a misleading price)
  } else if (typeof promotedVariantPackInfo.packPriceInMicros === 'number') {
    promotionFullPrice = promotedVariantPackInfo.packPriceInMicros * unitCount;
  } else if (typeof basePriceInMicros !== 'number') {
    promotionFullPrice = null;
  } else if (typeof promotedVariantPackInfo.discountInMicros === 'number') {
    // discount is on the full price, so subtract discount from full price
    // before dividing by unitCount to get unit price
    const fullItemPrice = basePriceInMicros * unitCount;
    promotionFullPrice =
      fullItemPrice - promotedVariantPackInfo.discountInMicros;
  } else if (typeof promotedVariantPackInfo.discountPercentage === 'number') {
    const discountAsPercent = promotedVariantPackInfo.discountPercentage / 100;

    const discountedPricePerUnit =
      basePriceInMicros - basePriceInMicros * discountAsPercent;

    promotionFullPrice = discountedPricePerUnit * unitCount;
  } else {
    // no discount, return null
    return null;
  }

  // Prevent displaying negative priced items
  if (typeof promotionFullPrice === 'number' && promotionFullPrice < 0) {
    return null;
  }

  if (typeof promotionFullPrice !== 'number') {
    return null;
  }

  return roundMicrosToNearestWholeCent(promotionFullPrice) / unitCount;
};

export interface OrderFee {
  amount_at_invoice_micros?: number | null;
  amount_at_order_micros?: number | null;
  display_type?: OrderFeeDisplayType | string | null;
  name?: string | null;
  uuid?: string | null;
}

interface DisplayTotalPriceParams {
  orderFees: OrderFee[];
  orderItems: OrderItemPriceFieldsFragment[];
}

interface DisplayTotalPriceOptions {
  displayItemPrices: boolean;
  displayPlaceholder?: boolean;
  hideMissingItems?: boolean;
}

export const displayTotalPrice = (
  { orderFees, orderItems }: DisplayTotalPriceParams,
  options: DisplayTotalPriceOptions,
) => {
  const currencyCode = orderItems.reduce<string | null>((result, oi) => {
    return result || oi.order_item_prices?.currency_code || null;
  }, null);

  const pricesMissing =
    !options.hideMissingItems &&
    orderItems.some((oi) => {
      const itemPrices = getOrderItemPricesFieldsToUse(oi);
      const hasDiscountPrice =
        typeof oi.order_item_prices?.unit_discount_price_at_order_micros ===
        'number';
      const unitPriceInMicros = itemPrices?.unitPriceInMicros;
      return (
        getTotalLineItemPrice({
          quantity: itemPrices?.quantity ?? 0,
          unitCount: oi.pricing_pack?.unit_count ?? oi.pack.unit_count,
          unitPriceInMicros:
            hasDiscountPrice || options.displayItemPrices
              ? unitPriceInMicros
              : null,
        }) === null
      );
    });

  if (pricesMissing) {
    return (options.displayPlaceholder ?? true)
      ? displayCurrencyPlaceholder(currencyCode)
      : '';
  }

  const totalFee = orderFees.find(
    (f) => f.display_type === OrderFeeDisplayType.Total,
  );
  return formatCurrency(
    totalFee?.amount_at_invoice_micros ?? totalFee?.amount_at_order_micros ?? 0,
    currencyCode,
  );
};

export const getItemSubtitle = ({
  displayString,
  unitPriceInMicros,
  currencyCode,
  unitCount,
}: ItemSubtitleParamsType) => {
  if (!unitPriceInMicros) {
    return displayString;
  }

  const formattedPrice = formatCurrency(
    unitPriceInMicros * unitCount,
    currencyCode || DEFAULT_CURRENCY_CODE,
  );

  return `${formattedPrice} / ${displayString}`;
};

interface PackPriceDisplayParams {
  baseUnit?: PackUnit | null;
  currencyCode?: string | null;
  promotionPricePerUnitInMicros?: number | null;
  unitCount: number;
  unitPriceInMicros?: number | null;
}

export interface PackPriceDisplay {
  // Pack price at the list unit price. Null when there is no unit price, or when
  // the promotion price rounds to the same cent and only it should be shown.
  formattedPackPrice: string | null;
  // Pack price at the promotion unit price. Null when there is no promotion.
  formattedPromotionPackPrice: string | null;
  // True when formattedPackPrice should render struck through.
  isPackPriceDiscounted: boolean;
  // Per-unit copy, e.g. "$50.00/lb". Null without a base unit or a unit price.
  perUnitPriceCopy: string | null;
  // True when perUnitPriceCopy carries <Text> emphasis markup, which has to be
  // rendered through MultiStyleText rather than a plain text node.
  perUnitPriceHasEmphasis: boolean;
}

// Every list item that shows a pack price beside its unit of measure resolves it
// here, so the same item priced in two different lists can't drift apart.
export const getPackPriceDisplay = ({
  baseUnit,
  currencyCode,
  promotionPricePerUnitInMicros,
  unitCount,
  unitPriceInMicros,
}: PackPriceDisplayParams): PackPriceDisplay => {
  const code = currencyCode || DEFAULT_CURRENCY_CODE;
  // 0.00 is a valid promotion price, so check the type rather than truthiness
  const promotionPriceInMicros =
    typeof promotionPricePerUnitInMicros === 'number'
      ? promotionPricePerUnitInMicros
      : null;

  const showOnlyPromotionPrice = Boolean(
    promotionPriceInMicros !== null &&
      unitPriceInMicros &&
      roundMicrosToNearestWholeCent(unitPriceInMicros) ===
        roundMicrosToNearestWholeCent(promotionPriceInMicros),
  );

  const formattedPromotionPackPrice =
    promotionPriceInMicros !== null
      ? formatCurrency(unitCount * promotionPriceInMicros, code)
      : null;

  const formattedPackPrice =
    typeof unitPriceInMicros === 'number' && !showOnlyPromotionPrice
      ? formatCurrency(unitCount * unitPriceInMicros, code)
      : null;

  let perUnitPriceCopy: string | null = null;
  let perUnitPriceHasEmphasis = false;

  // Always show the per-unit price when a base unit is available
  if (baseUnit && typeof unitPriceInMicros === 'number') {
    const unit = (LOCALIZED_PACK_UNITS[baseUnit] || baseUnit).toLowerCase();

    if (promotionPriceInMicros && !showOnlyPromotionPrice) {
      perUnitPriceCopy = i18n.t('per_unit_promo_price', {
        previousPrice: formatCurrency(unitPriceInMicros, code),
        promoPrice: formatCurrency(promotionPriceInMicros, code),
        unit,
      });
      perUnitPriceHasEmphasis = true;
    } else {
      perUnitPriceCopy = i18n.t('per_unit_price', {
        price: formatCurrency(
          promotionPriceInMicros || unitPriceInMicros,
          code,
        ),
        unit,
      });
    }
  }

  return {
    formattedPackPrice,
    formattedPromotionPackPrice,
    isPackPriceDiscounted: promotionPriceInMicros !== null,
    perUnitPriceCopy,
    perUnitPriceHasEmphasis,
  };
};

export const displayCurrencyPlaceholder = (currencyCode?: string | null) => {
  const code = currencyCode?.toUpperCase() || DEFAULT_CURRENCY_CODE;
  const formatter = getNumberFormatter(i18n.locale, {
    currency: code,
    style: 'currency',
  });

  const overrideSymbol = CURRENCY_SYMBOL_OVERRIDES[code];

  const parts = formatter.formatToParts(undefined);
  return parts
    .map((x) => {
      if (x.type === 'nan') {
        return ' -- ';
      }
      if (x.type === 'currency' && overrideSymbol) {
        return overrideSymbol;
      }
      return x.value;
    })
    .filter((x) => x.trim())
    .join('')
    .trim();
};

export const displayLineItemPrice = (
  priceFields: PriceFieldsType,
  displayPlaceholder = true,
) => {
  const totalPriceInMicros = getTotalLineItemPrice(priceFields);

  return typeof totalPriceInMicros === 'number'
    ? formatCurrency(
        totalPriceInMicros,
        priceFields.currencyCode || DEFAULT_CURRENCY_CODE,
      )
    : displayPlaceholder
      ? displayCurrencyPlaceholder(priceFields.currencyCode)
      : '';
};

export const getCurrencySymbol = (currencyCode?: string | null) => {
  const code = currencyCode?.toUpperCase() || DEFAULT_CURRENCY_CODE;
  const overrideSymbol = CURRENCY_SYMBOL_OVERRIDES[code];
  if (overrideSymbol) {
    return overrideSymbol;
  }

  const formatter = getNumberFormatter(i18n.locale, {
    currency: code,
    style: 'currency',
  });

  return (
    formatter.formatToParts(undefined).find((x) => x.type === 'currency')
      ?.value ?? DEFAULT_CURRENCY_SYMBOL
  );
};

export const roundMicrosToNearestWholeCent = (
  amountInMicros: number,
): number => {
  return Math.round(amountInMicros / ONE_CENT) * ONE_CENT;
};

// Stored prices may carry sub-cent micros while display formatters render
// at 2-decimal precision; normalize both sides before comparing.
export const getNormalizedPriceInMicros = (
  priceInMicros: number | null,
): number | null =>
  priceInMicros === null ? null : roundMicrosToNearestWholeCent(priceInMicros);

export const roundToNearestHundredth = (value: number) => {
  return Math.round(value * 100) / 100;
};

/**
 * Convert from price in micros to gross profit
 * @param {number} costInMicros Cost of goods in micros
 * @param {number} priceInMicros Price of goods in micros
 * @returns {number} Calculated gross profit in percent
 */
export const convertPriceMicrosToGrossProfit = (
  costInMicros: number,
  priceInMicros: number,
): number => {
  return (1 - costInMicros / priceInMicros) * 100;
};

/**
 * Convert from gross profit percent to price in micros
 * @param {number} costInMicros Cost of goods in micros
 * @param {number} grossProfitPercent  Percentage gross profit
 * @returns {number} Price of goods in micros
 */
export const convertGrossProfitToPriceMicros = (
  costInMicros: number,
  grossProfitPercent: number,
): number => {
  return costInMicros / (1 - grossProfitPercent / 100);
};

export const formatPriceInMicrosWithoutSymbol = (
  priceInMicros: number,
  currencyCode?: string | null,
) => {
  const currencyFormat = getNumberFormatter(i18n.locale, {
    currency: currencyCode || DEFAULT_CURRENCY_CODE,
    style: 'currency',
  });

  const resolvedOptions = currencyFormat.resolvedOptions();

  return getNumberFormatter(i18n.locale, {
    maximumFractionDigits: resolvedOptions.maximumFractionDigits,
    useGrouping: false,
  }).format(priceInMicros / MICRO_CONVERSION);
};

/**
 * Format a price in micros to a standard display value
 *
 * @param {number} priceInMicros Price in micros
 *
 * @returns {string} Formatted price in dollars and cents $XX.YY
 */
export const formatPriceDisplayValue = (priceInMicros?: number | null) => {
  if (typeof priceInMicros !== 'number') {
    return '';
  }

  const convertedPrice = priceInMicros / MICRO_CONVERSION;

  return getNumberFormatter()
    .formatToParts(convertedPrice)
    .filter((part) => part.type !== 'currency') // remove currency from formatted price
    .map((part) => part.value)
    .join('');
};

/**
 * Format a gross profit percent to a standard display value
 *
 * @param {number} grossProfitPercent Gross profit percent
 *
 * @returns {string} Formatted gross profit percent XX.YY
 */
export const formatGrossProfitDisplayValue = (
  grossProfitPercent?: number | null,
) => {
  if (typeof grossProfitPercent !== 'number') {
    return '';
  }

  return toFixedDecimalPercentage(grossProfitPercent).replace('%', '');
};

/**
 * Format a unit price breakdown for display.
 *
 * @example
 * formatUnitPriceBreakdown({
 *   quantity: 80.456,
 *   unitPriceInMicros: 1600000,
 *   unit: 'lb',
 *   currencyCode: 'USD',
 * }) // Returns "80.46 × $1.60/lb"
 */
export const formatUnitPriceBreakdown = ({
  quantity,
  unitPriceInMicros,
  unit,
  currencyCode,
}: {
  currencyCode?: string | null;
  quantity: number;
  unit: string;
  unitPriceInMicros: number;
}): string => {
  const roundedQuantity = parseFloat(quantity.toFixed(2));
  const formattedPrice = formatCurrency(unitPriceInMicros, currencyCode);
  return `${roundedQuantity} × ${formattedPrice}/${unit}`;
};
